/**
 * HealthBadge.tsx — Live per-account health FSM badge (D-18).
 *
 * Subscribes to WS `health:update` events for the given accountId.
 * Dead accounts are visibly dead (red DEAD label).
 * State colors:
 *   online        → green
 *   reconnecting  → amber
 *   needs_reauth  → red
 *   cf_blocked    → purple
 *   dead          → grey (bold DEAD)
 *   connecting    → blue
 */

import * as React from 'react';
import { useWsClient } from '../lib/WsProvider.js';
import type { HealthState } from '@crm/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HealthBadgeProps {
  accountId: string;
  /** Initial state (from REST GET /coverage or /accounts) */
  initialState: HealthState;
  /** If true, shows the reason string if available */
  showReason?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Style map
// ---------------------------------------------------------------------------

const HEALTH_CONFIG: Record<
  HealthState,
  { label: string; dotClass: string; textClass: string; bg: string }
> = {
  online: {
    label: 'Online',
    dotClass: 'bg-online',
    textClass: 'text-online',
    bg: 'bg-online/10 border-online/30',
  },
  reconnecting: {
    label: 'Reconnecting',
    dotClass: 'bg-reconnecting animate-pulse',
    textClass: 'text-reconnecting',
    bg: 'bg-reconnecting/10 border-reconnecting/30',
  },
  needs_reauth: {
    label: 'Needs Re-auth',
    dotClass: 'bg-needs-reauth animate-pulse',
    textClass: 'text-needs-reauth',
    bg: 'bg-needs-reauth/10 border-needs-reauth/30',
  },
  cf_blocked: {
    label: 'CF Blocked',
    dotClass: 'bg-cf-blocked',
    textClass: 'text-cf-blocked',
    bg: 'bg-cf-blocked/10 border-cf-blocked/30',
  },
  dead: {
    label: 'DEAD',
    dotClass: 'bg-dead',
    textClass: 'text-dead font-bold',
    bg: 'bg-dead/10 border-dead/30',
  },
  connecting: {
    label: 'Connecting',
    dotClass: 'bg-connecting animate-pulse',
    textClass: 'text-connecting',
    bg: 'bg-connecting/10 border-connecting/30',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HealthBadge({
  accountId,
  initialState,
  showReason = false,
  className = '',
}: HealthBadgeProps) {
  const ws = useWsClient();
  const [state, setState] = React.useState<HealthState>(initialState);
  const [reason, setReason] = React.useState<string | undefined>();

  React.useEffect(() => {
    const unsub = ws.onHealthUpdate((evt) => {
      if (evt.accountId !== accountId) return;
      setState(evt.state);
      setReason(evt.reason);
    });
    return unsub;
  }, [ws, accountId]);

  // Reset when accountId changes (e.g. re-used component)
  React.useEffect(() => {
    setState(initialState);
    setReason(undefined);
  }, [accountId, initialState]);

  const cfg = HEALTH_CONFIG[state] ?? HEALTH_CONFIG.dead;

  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs',
        cfg.bg,
        cfg.textClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      title={reason ?? cfg.label}
    >
      <span
        className={['w-1.5 h-1.5 rounded-full shrink-0', cfg.dotClass].join(' ')}
        aria-hidden="true"
      />
      {cfg.label}
      {showReason && reason && (
        <span className="text-text-subtle ml-1">({reason})</span>
      )}
    </span>
  );
}

/**
 * Inline variant — returns just the CSS class string for embedding in table cells
 * without full badge wrapping. Useful for compact table cells.
 */
export function healthStateLabel(state: HealthState): string {
  return HEALTH_CONFIG[state]?.label ?? state;
}
